import { NavContext } from '@ionic/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

import { Capacitor } from '@capacitor/core';

import { useToast } from '@/hooks';
import { PLATFORMS, STORED_ACCOUNTS_KEY } from '@/utils/constants';
import { getUserMobileNumber } from '@/utils/helpers/getUserMobileNumber';
import { ROUTES } from '@/utils/routes';
import { supabase } from '@/utils/supabase';
import { StoredAccount } from '@/utils/types';
import { useQueryClient } from '@tanstack/react-query';
import { SecurePreferences } from 'capacitor-secure-preferences';

interface AccountContextType {
  shouldSaveAccount: boolean;
  setShouldSaveAccount: React.Dispatch<React.SetStateAction<boolean>>;
  storedAccounts: StoredAccount[];
  loading: boolean;
  saveAccount: (displayName: string) => Promise<void>;
  removeAccount: (userId: string) => Promise<void>;
  clearAllAccounts: () => Promise<void>;
  getStoredAccounts: () => Promise<void>;
  switchToAccount: (account: StoredAccount) => Promise<void>;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

interface AccountProviderProps {
  children: React.ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({
  children
}) => {
  if (Capacitor.getPlatform() === PLATFORMS.WEB) {
    return (
      <AccountContext.Provider
        value={{
          shouldSaveAccount: false,
          setShouldSaveAccount: (
            _value: boolean | ((prev: boolean) => boolean)
          ) => {
            // No-op for web platform
          },
          storedAccounts: [],
          loading: false,
          saveAccount: async (_displayName: string) => {
            // No-op for web platform
          },
          removeAccount: async (_userId: string) => {
            // No-op for web platform
          },
          clearAllAccounts: async () => {
            // No-op for web platform
          },
          getStoredAccounts: async () => {
            // No-op for web platform
          },
          switchToAccount: async (_account: StoredAccount) => {
            // No-op for web platform
          }
        }}
      >
        {children}
      </AccountContext.Provider>
    );
  }

  const { navigate } = useContext(NavContext);
  const displayToast = useToast();
  const queryClient = useQueryClient();

  const [shouldSaveAccount, setShouldSaveAccount] = useState<boolean>(false);
  const [storedAccounts, setStoredAccounts] = useState<StoredAccount[]>([]);
  const [loading, setLoading] = useState(false);

  const getStoredAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const { value } = await SecurePreferences.get({
        key: STORED_ACCOUNTS_KEY
      });

      if (value) {
        try {
          const parsedAccounts = JSON.parse(value);

          const sortedAccounts = parsedAccounts.sort(
            (a: StoredAccount, b: StoredAccount) =>
              a.displayName.localeCompare(b.displayName)
          );
          setStoredAccounts(sortedAccounts);
        } catch (parseError) {
          // If parsing fails, clear all saved accounts
          await SecurePreferences.remove({ key: STORED_ACCOUNTS_KEY });
          setStoredAccounts([]);
        }
      } else {
        setStoredAccounts([]);
      }
    } catch {
      setStoredAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveAccount = useCallback(
    async (displayName: string) => {
      try {
        const {
          data: { user }
        } = await supabase.auth.getUser();
        const {
          data: { session }
        } = await supabase.auth.getSession();
        const phone = getUserMobileNumber(user);

        if (
          !phone ||
          !user?.id ||
          !session?.access_token ||
          !session?.refresh_token
        ) {
          throw new Error('No current user or session found');
        }

        const newAccount: StoredAccount = {
          userId: user.id,
          phone,
          displayName,
          accessToken: session.access_token,
          refreshToken: session.refresh_token,
          lastUsed: Date.now()
        };

        const existingIndex = storedAccounts.findIndex(
          (storedAccount) => storedAccount.userId === user.id
        );

        let updatedAccounts: StoredAccount[];
        if (existingIndex >= 0) {
          // Update existing account for the same user
          updatedAccounts = [...storedAccounts];
          updatedAccounts[existingIndex] = newAccount;
        } else {
          // Add new account (different user, even if same display name)
          updatedAccounts = [...storedAccounts, newAccount];
        }

        await SecurePreferences.set({
          key: STORED_ACCOUNTS_KEY,
          value: JSON.stringify(updatedAccounts)
        });

        setStoredAccounts(updatedAccounts);
      } catch (error) {
        displayToast({
          message: 'Failed to save account. Please try again.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    },
    [storedAccounts, displayToast]
  );

  const clearAllAccounts = useCallback(async () => {
    try {
      await SecurePreferences.remove({ key: STORED_ACCOUNTS_KEY });
      setStoredAccounts([]);
    } catch (error) {
      displayToast({
        message: 'Failed to clear accounts. Please try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  }, [displayToast]);

  const removeAccount = useCallback(
    async (userId: string) => {
      try {
        const updatedAccounts = storedAccounts.filter(
          (account) => account.userId !== userId
        );

        await SecurePreferences.set({
          key: STORED_ACCOUNTS_KEY,
          value: JSON.stringify(updatedAccounts)
        });

        setStoredAccounts(updatedAccounts);
      } catch (error) {
        displayToast({
          message: 'Failed to remove account. Please try again.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    },
    [storedAccounts, displayToast]
  );

  const switchToAccount = useCallback(
    async (account: StoredAccount) => {
      setLoading(true);
      try {
        const {
          data: { user: currentUser }
        } = await supabase.auth.getUser();

        if (currentUser?.id === account.userId) {
          displayToast({
            message: `Switched to ${account.displayName}`,
            duration: 2400,
            position: 'bottom',
            positionAnchor: 'tab-bar'
          });
          return;
        }

        const {
          data: { session },
          error
        } = await supabase.auth.setSession({
          access_token: account.accessToken,
          refresh_token: account.refreshToken
        });

        if (!session?.access_token || !session?.refresh_token || error) {
          // If session setting fails, assume the session is expired
          await removeAccount(account.userId);
          displayToast({
            message: 'Account session expired. Please sign in again.',
            duration: 3000,
            position: 'bottom',
            positionAnchor: 'tab-bar'
          });
          navigate(ROUTES.ADD_ACCOUNT);
          return;
        }

        // Update saved account with new access and refresh tokens
        const updatedAccount = {
          ...account,
          accessToken: session.access_token,
          refreshToken: session.refresh_token,
          lastUsed: Date.now()
        };
        const updatedAccounts = storedAccounts.map((acc) =>
          acc.userId === account.userId ? updatedAccount : acc
        );

        await SecurePreferences.set({
          key: STORED_ACCOUNTS_KEY,
          value: JSON.stringify(updatedAccounts)
        });
        setStoredAccounts(updatedAccounts);

        queryClient.invalidateQueries({ queryKey: ['auth', 'user'] });
        navigate(ROUTES.SELECT_DENTIST);
        displayToast({
          message: `Switched to ${account.displayName}`,
          duration: 2400,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      } catch (error) {
        displayToast({
          message: 'Failed to switch account. Please try again.',
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      } finally {
        setLoading(false);
      }
    },
    [queryClient, navigate, displayToast, removeAccount, storedAccounts]
  );

  useEffect(() => {
    getStoredAccounts();
  }, [getStoredAccounts]);

  return (
    <AccountContext.Provider
      value={{
        shouldSaveAccount,
        setShouldSaveAccount,
        storedAccounts,
        loading,
        saveAccount,
        removeAccount,
        clearAllAccounts,
        getStoredAccounts,
        switchToAccount
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};
